/**
 * Pengelola tema terang/gelap.
 *
 * Nilai awalnya sudah ditetapkan skrip inline di index.html sebelum paint
 * pertama, jadi hook ini hanya MEMBACA apa yang sudah terpasang di <html>.
 * Menentukan ulang di sini akan menyebabkan kedip.
 */

import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "ag5r:theme";

function currentTheme(): Theme {
  const attr = document.documentElement.getAttribute("data-theme");
  return attr === "light" ? "light" : "dark";
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
  // Warna bar browser di mobile ikut menyesuaikan.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", theme === "light" ? "#F6F8FC" : "#0B0F1A");
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage diblokir — tema tetap berlaku sampai halaman ditutup.
  }
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(currentTheme);

  // Ikuti perubahan preferensi sistem, tapi HANYA kalau user belum pernah
  // memilih sendiri. Pilihan eksplisit tidak boleh ditimpa OS.
  useEffect(() => {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(STORAGE_KEY);
    } catch {
      saved = null;
    }
    if (saved === "light" || saved === "dark") return;

    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e: MediaQueryListEvent) => {
      const next: Theme = e.matches ? "light" : "dark";
      document.documentElement.setAttribute("data-theme", next);
      setTheme(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      applyTheme(next);
      return next;
    });
  }, []);

  return { theme, toggle };
}
